import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";
import type { SaleInstallmentStatus } from "@/lib/sales/saleInstallments";

export type SaleInstallmentRecord = {
  id: number;
  installment_no: number;
  due_date: string;
  amount: number;
  paid_amount: number;
  penalty_amount?: number;
  penalty_paid?: number;
  remaining_amount?: number;
  remaining_penalty?: number;
  status: SaleInstallmentStatus;
  paid_at?: string | null;
};

export type ProfitDistributionStatus = "pending" | "partially_paid" | "paid" | "cancelled";

export type SaleProfitDistributionRecord = {
  id: number;
  sale_id: number;
  partner_id: number;
  party_id?: number | null;
  party_name?: string | null;
  partner_profit_type?: string | null;
  partner_profit_value?: number | null;
  investment_amount?: number | null;
  purchase_ratio?: number | null;
  profit_ratio?: number | null;
  profit_amount: number;
  final_payable: number;
  paid_amount: number;
  balance: number;
  allocated_active?: number | null;
  available?: number | null;
  status: ProfitDistributionStatus | string;
};

export type ProfitDistributionRecipient = {
  name: string;
  partner_id?: number | null;
  party_id?: number | null;
  company_id?: number | null;
  percent?: number | null;
  profit_ratio?: number | null;
  amount?: number | null;
  final_payable?: number | null;
  investment_amount?: number | null;
  purchase_ratio?: number | null;
  note?: string | null;
  commission_type?: string | null;
  commission_value?: number | null;
  /** Amount of broker commission taken from this recipient's share. */
  broker_commission_deducted?: number | null;
  /** Share amount before broker commission was deducted. */
  amount_before_broker?: number | null;
  final_payable_before_broker?: number | null;
};

export type ProfitDistributionSummary = {
  deal_type?: string | null;
  profit_rule_type?: string | null;
  explanation?: string | null;
  sale_price?: number | null;
  purchase_cost?: number | null;
  additional_costs?: number | null;
  broker_commission?: number | null;
  broker_commission_payer?: "seller" | "showroom" | "overall" | null;
  /** Sale − landed cost, before overall broker deduction. */
  gross_profit?: number | null;
  net_profit?: number | null;
  investor_surplus?: number | null;
  extra_profit?: number | null;
  /** Cost-line over-receives that make up extra_profit (showroom). */
  extra_profit_details?: Array<{
    id: number;
    cost_label: string;
    party_name?: string | null;
    actual_amount: number;
    received_amount: number;
    profit_amount: number;
    payment_date?: string | null;
  }> | null;
  owner_demand_price?: number | null;
  owner_payable?: {
    owner_demand_price?: number | null;
    total_payable?: number | null;
  } | null;
  surplus_distribution?: {
    surplus?: number | null;
    showroom_percent?: number | null;
    showroom_amount?: number | null;
  } | null;
  profit_summary?: {
    purchase_cost?: number | null;
    additional_costs?: number | null;
    owner_base_profit?: number | null;
    owner_actual_profit?: number | null;
    showroom_profit?: number | null;
    total_profit?: number | null;
  } | null;
  showroom?: ProfitDistributionRecipient | null;
  partners?: ProfitDistributionRecipient[];
  distribution_total?: number | null;
};

export type SalePaymentRecord = {
  id: number;
  product_id?: number | null;
  sale_id?: number | null;
  party_id?: number | null;
  party_name?: string | null;
  direction: "debit" | "credit";
  amount: number;
  debit?: number;
  credit?: number;
  transaction_type: string | null;
  transaction_type_label?: string | null;
  payment_date: string | null;
  payment_method?: string | null;
  payment_method_label?: string | null;
  bank_id?: number | null;
  bank_name?: string | null;
  transaction_reference_no?: string | null;
  pay_order_no?: string | null;
  notes?: string | null;
  created_at?: string | null;
};

export type SalePaymentStatus = "unpaid" | "partial" | "paid" | "cancelled" | "returned";

export type SaleInvoiceRecord = {
  id: number;
  invoice_no?: string;
  product_id: number | null;
  customer_id: number | null;
  employee_id: number | null;
  broker_party_id?: number | null;
  broker_commission?: number | null;
  broker_commission_payer?: "seller" | "showroom" | "overall" | null;
  broker_commission_paid?: number | null;
  broker_commission_outstanding?: number | null;
  broker?: {
    id: number;
    name: string;
    phone?: string | null;
  } | null;
  date: string | null;
  total_amount: number;
  token_amount?: number | null;
  token_type?: string | null;
  balance_amount?: number | null;
  is_installment?: boolean;
  down_payment?: number | null;
  down_payment_paid?: number | null;
  down_payment_outstanding?: number | null;
  installment_count?: number | null;
  installment_first_due_date?: string | null;
  payable_date?: string | null;
  delivery_date?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: number | null;
  cancel_notes?: string | null;
  is_cancelled?: boolean;
  is_returned?: boolean;
  void_type?: "cancel" | "return" | string | null;
  sale_return?: {
    id: number;
    sale_id: number;
    return_date?: string | null;
    sale_amount: number;
    collected_amount: number;
    refunded_amount: number;
    retained_amount: number;
    notes?: string | null;
    refund_payments?: Array<{
      id: number;
      amount: number;
      payment_method?: string | null;
      payment_method_label?: string | null;
      bank_name?: string | null;
      payment_date?: string | null;
      notes?: string | null;
    }>;
  } | null;
  payment_method?: string | null;
  bank_name?: string | null;
  transaction_reference_no?: string | null;
  pay_order_no?: string | null;
  description: string | null;
  installments?: SaleInstallmentRecord[];
  installment_paid_total?: number | null;
  installment_outstanding?: number | null;
  balance_paid_total?: number | null;
  balance_outstanding?: number | null;
  paid_total?: number | null;
  outstanding?: number | null;
  payment_status?: SalePaymentStatus | null;
  payments?: SalePaymentRecord[];
  product?: {
    id: number;
    name: string;
    image_url?: string | null;
    deal_type?: string | null;
    profit_rule_type?: string | null;
    owner_demand_price?: number | null;
    landed_cost?: number | null;
    purchase_price?: number | null;
    category_id?: number | null;
    inventory_status?: string | null;
    attributes?: Array<{ code: string; value: any }>;
  } | null;
  customer?: { id: number; name: string; phone?: string | null; email?: string | null; city?: string | null } | null;
  employee?: { id: number; full_name?: string | null; employee_code?: string | null } | null;
  profit_distribution_summary?: ProfitDistributionSummary | null;
  profit_distributions?: SaleProfitDistributionRecord[];
  profit_distributions_total_payable?: number | null;
  profit_distributions_total_profit?: number | null;
  profit_distributions_total_paid?: number | null;
  profit_distributions_total_balance?: number | null;
};

export function saleInvoiceErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export async function fetchSaleInvoice(id: string | number): Promise<SaleInvoiceRecord | null> {
  const response = await api.get<{ data?: SaleInvoiceRecord }>(`/api/v1/sales/${id}/invoice`);
  return response.data?.data ?? null;
}

export async function updateSaleDelivery(
  saleId: string | number,
  deliveryDate: string,
  deliveryChecklist?: Record<string, any>,
): Promise<{ message: string; record: SaleInvoiceRecord | null }> {
  const response = await api.patch<{ message?: string; data?: SaleInvoiceRecord }>(
    `/api/v1/sales/${saleId}/delivery`,
    {
      delivery_date: deliveryDate,
      delivery_checklist: deliveryChecklist,
    },
  );
  return {
    message: response.data?.message ?? "Delivery date updated.",
    record: response.data?.data ?? null,
  };
}

export async function cancelSale(
  saleId: string | number,
  payload: {
    cancel_date?: string | null;
    notes?: string | null;
    payment_method?: string | null;
    bank_id?: number | null;
  } = {},
): Promise<{ message: string; record: SaleInvoiceRecord | null }> {
  const response = await api.post<{ message?: string; data?: SaleInvoiceRecord }>(
    `/api/v1/sales/${saleId}/cancel`,
    payload,
  );
  return {
    message: response.data?.message ?? "Sale cancelled.",
    record: response.data?.data ?? null,
  };
}

export async function returnSale(
  saleId: string | number,
  payload: {
    return_date?: string | null;
    notes?: string | null;
    payment_method?: string | null;
    bank_id?: number | null;
  } = {},
): Promise<{ message: string; record: SaleInvoiceRecord | null }> {
  const response = await api.post<{ message?: string; data?: SaleInvoiceRecord }>(
    `/api/v1/sales/${saleId}/return`,
    payload,
  );
  return {
    message: response.data?.message ?? "Sale returned.",
    record: response.data?.data ?? null,
  };
}

export { paySaleInstallment, paySaleBalance, payBrokerCommission } from "@/lib/sales/salesStore";
export type { SaleBalancePaymentPayload } from "@/lib/sales/salesStore";
